"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { multiSelect } from './styles.js';
import { Box } from '../Box/Box.web.js';
import { useBoolean } from '../utils/hooks/useBoolean.js';
import { useEventTargetValue } from '../utils/hooks/useEventTargetValue.js';
import { ListItems } from '../utils/components/ListItems/ListItems.web.js';
import { ThemeProvider } from 'styled-components';
import { useTheme } from '../utils/theming/useTheme.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.web.js';
import { ContentText } from '../ContentText/ContentText.web.js';
import { useSelectAll, draftEvent } from '../Select/utils.js';
import { filterItemsMultiselect } from './utils.js';
import { useIsomorphicLayoutEffect } from '../utils/hooks/useIsoMorphicLayoutEffect.web.js';
import { useSmartControlledEventHandler } from '../utils/hooks/useSmartControlledEventHandler.web.js';
import { InputField } from '../InputField/InputField.web.js';

var _excluded = ["label", "name", "items", "selected", "filterItems", "onChange", "maxItems", "showItems", "id", "readOnly", "color", "required", "disabled", "error", "errorMessageProps", "errorMessage", "marginBottom", "mv", "marginVertical", "m", "margin", "placeholder", "borderRadius", "borderStyle", "selectAll", "renderItem", "size", "variant"];
function Multiselect(props) {
  var label = props.label,
    name = props.name,
    itemsProp = props.items,
    selectedProp = props.selected,
    filterItemsProp = props.filterItems,
    onChangeProp = props.onChange,
    maxItems = props.maxItems,
    _props$showItems = props.showItems,
    showItems = _props$showItems === undefined ? 5 : _props$showItems,
    id = props.id,
    readOnly = props.readOnly,
    color = props.color,
    required = props.required,
    disabled = props.disabled,
    error = props.error,
    errorMessageProps = props.errorMessageProps,
    errorMessage = props.errorMessage,
    marginBottomProp = props.marginBottom,
    mvProp = props.mv,
    marginVerticalProp = props.marginVertical,
    mProp = props.m,
    marginProp = props.margin,
    placeholder = props.placeholder,
    borderRadiusProp = props.borderRadius,
    borderStyleProp = props.borderStyle,
    selectAll = props.selectAll,
    renderItem = props.renderItem,
    sizeProp = props.size,
    variantProp = props.variant,
    rest = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var variant = variantProp || theme.variant;
  var size = variant === 'condensed' ? 's' : sizeProp !== null && sizeProp !== undefined ? sizeProp : theme.preferredComponentSize;
  var _useBoolean = useBoolean(false),
    opened = _useBoolean.value,
    handleClose = _useBoolean.setFalse,
    handleOpen = _useBoolean.setTrue;
  var borderStyle = opened ? 'full' : borderStyleProp || variant === 'condensed' ? 'bottomOnly' : theme.select.web.borderStyle;
  var borderRadius = (borderRadiusProp !== null && borderRadiusProp !== undefined ? borderRadiusProp : borderStyle === 'bottomOnly') ? 0 : theme.borders.radius;
  var _useEventTargetValue = useEventTargetValue(''),
    _useEventTargetValue2 = _slicedToArray(_useEventTargetValue, 2),
    autoCompleteValue = _useEventTargetValue2[0],
    setAutoCompleteValue = _useEventTargetValue2[1];
  var inputRef = React.useRef(null);
  var errorMsgRef = React.useRef(null);
  var wrapperRef = React.useRef(null);
  var _React$useState = React.useState(undefined),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    activeElement = _React$useState2[0],
    setActiveElement = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    disableActive = _React$useState4[0],
    setDisableActive = _React$useState4[1];
  var _React$useState5 = React.useState(0),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    errorMargin = _React$useState6[0],
    setErrorMargin = _React$useState6[1];
  var _useSmartControlledEv = useSmartControlledEventHandler([], selectedProp, onChangeProp),
    _useSmartControlledEv2 = _slicedToArray(_useSmartControlledEv, 2),
    selected = _useSmartControlledEv2[0],
    onChange = _useSmartControlledEv2[1];
  var _useSelectAll = useSelectAll(itemsProp, selected, selectAll),
    items = _useSelectAll.items,
    selectedItems = _useSelectAll.selectedItems,
    getNewValue = _useSelectAll.getNewValue;
  useIsomorphicLayoutEffect(function () {
    if (error) {
      var _errorMsgRef$current$, _errorMsgRef$current;
      // istanbul ignore next -- ?? 0 for sanity
      setErrorMargin((_errorMsgRef$current$ = (_errorMsgRef$current = errorMsgRef.current) === null || _errorMsgRef$current === undefined ? undefined : _errorMsgRef$current.getBoundingClientRect().height) !== null && _errorMsgRef$current$ !== undefined ? _errorMsgRef$current$ : 0);
    } else {
      setErrorMargin(0);
    }
  }, [errorMsgRef.current, error, errorMessage, errorMessageProps]);
  var filteredItems = React.useMemo(function () {
    return filterItemsMultiselect(items, autoCompleteValue, filterItemsProp, maxItems);
  }, [items, autoCompleteValue, filterItemsProp, maxItems]);
  var disableInputFieldBorder = (filteredItems.length > 0 || filteredItems.length === 0 && items.length > 0) && opened;
  var handleOnSelect = function handleOnSelect(value) {
    var newValue = getNewValue(value);
    onChange(draftEvent(newValue, name));
  };
  var handleContainerClick = function handleContainerClick() {
    // istanbul ignore else -- no need to test this separately
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  var handleKeyboardTopOut = function handleKeyboardTopOut() {
    // istanbul ignore else -- no need to test this separately
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  var handleInputKeyDown = React.useCallback(function (e) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && e.key !== 'Escape') {
      handleOpen();
      setDisableActive(true);
    } else {
      setDisableActive(false);
    }
  }, [handleOpen, setDisableActive]);
  var handleFocus = React.useCallback(function (e) {
    // istanbul ignore else
    if (!opened && wrapperRef.current && wrapperRef.current.contains(e.currentTarget)) {
      handleOpen();
    }
  }, [handleOpen, opened]);
  var handleBlur = React.useCallback(function (e) {
    // istanbul ignore else
    if (!(wrapperRef.current && wrapperRef.current.contains(e.relatedTarget))) {
      handleClose();
    }
  }, [handleClose]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(multiSelect, _extends({
    "data-testid": "elemental-Multiselect",
    onFocusCapture: handleFocus,
    onBlurCapture: handleBlur
    // // to prevent focus jumping from div to input
    ,
    onMouseDown:
    // istanbul ignore next - no need to test this separately
    function onMouseDown(e) {
      return e.preventDefault();
    },
    onMouseUp: handleContainerClick,
    ref: wrapperRef,
    id: id,
    $opened: opened,
    $error: error
  }, rest, {
    // adapt if any marginal prop is provided
    mv: mvProp || marginVerticalProp,
    m: mProp || marginProp,
    marginBottom: "calc(".concat(errorMargin, "px + ").concat(
    // eslint-disable-next-line @typescript-eslint/no-base-to-string,@typescript-eslint/restrict-template-expressions
    marginBottomProp || mvProp || marginVerticalProp || marginProp || mProp || '0px', ")")
  }), /*#__PURE__*/React.createElement(InputField, {
    autoComplete: "off",
    label: label,
    name: name,
    inputRef: inputRef,
    onKeyDown: handleInputKeyDown,
    onChange: setAutoCompleteValue,
    value: autoCompleteValue,
    borderStyle: borderStyle,
    disabled: disabled,
    readOnly: readOnly,
    placeholder: placeholder,
    required: required,
    display: "block",
    minWidth: "2rem",
    width: "unset",
    type: "text",
    color: color || theme.multiselect.web.color,
    borderRadius: borderRadius,
    disableBottomBorder: disableInputFieldBorder,
    disableBottomBorderRadius: disableInputFieldBorder,
    size: size,
    inputProps: {
      autoComplete: 'off',
      'aria-label': label,
      'aria-invalid': error,
      'aria-expanded': opened,
      'aria-describedby': error ? "elemental-Multiselect-".concat(id || name, "__error") : undefined,
      role: 'combobox',
      'aria-controls': opened ? "elemental-Multiselect-results-".concat(id || name) : undefined,
      'aria-activedescendant': opened ? activeElement === null || activeElement === undefined ? undefined : activeElement.id : undefined,
      'aria-autocomplete': 'list'
    },
    variant: variant
  }), /*#__PURE__*/React.createElement(Box, {
    hidden: !error
  }, /*#__PURE__*/React.createElement(ErrorMessage, _extends({
    errorRef: errorMsgRef,
    position: "absolute",
    active: true,
    id: "elemental-Multiselect-".concat(id || name, "__error")
  }, errorMessageProps), /*#__PURE__*/React.createElement(ContentText, null, errorMessage || 'Please check input'))), /*#__PURE__*/React.createElement(ListItems, {
    id: "elemental-Multiselect-results-".concat(id || name),
    "aria-label": label,
    "aria-multiselectable": true,
    disableFocusTransfer: true,
    borderRadius: borderRadius,
    highlightValue:
    // istanbul ignore next
    autoCompleteValue === '' ? undefined : autoCompleteValue,
    highlightSelected: true,
    items:
    // istanbul ignore next
    filteredItems.length ? filteredItems : items,
    selected: selectedItems.map(function (item) {
      return item.value;
    }),
    onSelect: handleOnSelect,
    onClose: handleClose,
    onActiveChange: function onActiveChange(el) {
      setActiveElement(el);
    },
    opened: opened,
    showItems: showItems,
    color: opened ? color || theme.multiselect.web.color : theme.multiselect.web.borders.inactiveColor,
    onKeyboardNavigateTopOut: handleKeyboardTopOut,
    selectWithSpacebar: false,
    disableActive: disableActive,
    renderItem: renderItem,
    size: size,
    variant: variant
  })));
}

export { Multiselect };
